import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 20,
    padding: 10,
    borderRadius: 5,
  },
  header: {
    fontSize: 16,
    marginBottom: 10,
    fontWeight: 'bold',
  },
  headers: {
    fontSize: 16,
    marginBottom: 10,
    fontWeight: 'bold',
    textAlign: 'center', // Align Zordigo in the center
  },
  subHeader: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  text: {
    fontSize: 10,
    marginBottom: 4,
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    borderStyle: 'solid',
  },
  tableCell: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#000',
    padding: 5,
    fontSize: 10,
  },
  tableCellHeader: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#000',
    padding: 5,
    fontSize: 10,
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
  },
  tableLastCell: {
    flex: 1,
    padding: 5,
    fontSize: 10,
  },
  totalCell: {
    flex: 1,
    padding: 5,
    fontSize: 10,
    fontWeight: 'bold',
    backgroundColor: '#c7e2ff',
  },
  flexRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  personalInfo: {
    backgroundColor: '#f0f8ff', // Light blue background
    padding: 10,
    borderRadius: 10,
  },
  orderDetails: {
    marginLeft: "50px",
    backgroundColor: '#f0f8ff', // Light gray background
    padding: 10,
    borderRadius: 10,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 10,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
});

// Helper function to create a table row
const TableRow = ({ children, style }) => (
  <View style={[styles.tableRow, style]}>
    {children}
  </View>
);

const PDFDocument = ({ data, deliver, failedOrders, reportId, order, working, active }) => {
  const parseReportId = (reportId) => {
    if (reportId.length < 7 || reportId.length > 8) {
      return { formattedDate: '', serialNumber: '' };
    }
    const datePart = reportId.slice(0, 6); // First 6 characters for the date
    const serialNumber = reportId.slice(6); // Remaining characters for the serial number

    const day = datePart.slice(0, 2);
    const month = datePart.slice(2, 4);
    const year = '20' + datePart.slice(4, 6); // Assuming year starts with '20'

    const formattedDate = `${day}/${month}/${year}`; // Format: 'dd/mm/yyyy'
    return { formattedDate, serialNumber };
  };

  // Extract report date and serial number
  const { formattedDate } = parseReportId(reportId);

  // Get unique payment modes
  const mopValues = Array.from(new Set(order.flatMap(item => [item.mop, item.mop2, item.mop3].filter(value => value && value !== 'null'))));

  // Calculate total amount for each payment mode
  const calculateTotalAmount = (paymentMode) => {
    return order.reduce((total, item) => {
      if (item.mop === paymentMode) {
        return total + (item.amount1 || 0); // Add amount1 when mop matches
      } else if (item.mop2 === paymentMode) {
        return total + (item.amount2 || 0); // Add amount2 when mop2 matches
      } else if (item.mop3 === paymentMode) {
        return total + (item.amount3 || 0); // Add amount3 when mop3 matches
      }
      return total;
    }, 0);
  };

  const currentDate = new Date().toLocaleString(); // Get current date and time

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.headers}>Zordigo</Text>
          <Text style={styles.header}>Driver Report</Text>
          <Text style={styles.text}>Date: {formattedDate}</Text>
          <Text style={styles.text}>Report ID: {reportId}</Text>
          <Text style={styles.text}>Working Hours: {working}</Text>
        </View>
        <View style={styles.flexRow}>
          <View style={[styles.personalInfo, { flex: 1 }]}>
            <Text style={styles.subHeader}>Personal Information</Text>
            <Text style={styles.text}>Name: {data.name}</Text>
            <Text style={styles.text}>Phone Number: {data.phone}</Text>
            <Text style={styles.text}>Email: {data.email}</Text>
            <Text style={styles.text}>Vehicle: {data.vehicle}</Text>
          </View>
          <View style={[styles.orderDetails, { flex: 1 }]}>
            <Text style={styles.subHeader}>Order Details</Text>
            <Text style={styles.text}>Total Orders: {deliver.assignedCount + deliver.completedCount + deliver.incompleteCount}</Text>
            <Text style={styles.text}>Active Orders: {deliver.assignedCount}</Text>
            <Text style={styles.text}>Completed Orders: {deliver.completedCount}</Text>
            <Text style={styles.text}>Incomplete Orders: {failedOrders.length}</Text>
          </View>
        </View>

        {mopValues.map((paymentMode, index) => (
          <View style={styles.section} key={index}>
            <Text style={styles.subHeader}>{index + 1}. {paymentMode}:</Text>
            <View style={styles.table}>
              <TableRow>
                <Text style={styles.tableCellHeader}>Order ID</Text>
                <Text style={styles.tableCellHeader}>Amount</Text>
              </TableRow>
              {order.filter(o => o.mop === paymentMode || o.mop2 === paymentMode || o.mop3 === paymentMode).map((item, idx) => (
                <TableRow key={idx}>
                  <Text style={styles.tableCell}>{item.Order_No}</Text>
                  <Text style={styles.tableCell}>
                    {item.mop === paymentMode ? item.amount1 : item.mop2 === paymentMode ? item.amount2 : item.mop3 === paymentMode ? item.amount3 : ''}
                  </Text>
                </TableRow>
              ))}
              {/* Display Total Amount for the payment mode */}
              <TableRow>
                <Text style={styles.tableLastCell}></Text>
                <Text style={styles.totalCell}>Total: {calculateTotalAmount(paymentMode)}</Text>
              </TableRow>
            </View>
          </View>
        ))}

        {failedOrders.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subHeader}>Failed Delivery Orders</Text>
            <View style={styles.table}>
              <TableRow>
                <Text style={styles.tableCellHeader}>Order ID</Text>
                <Text style={styles.tableCellHeader}>Reason</Text>
              </TableRow>
              {failedOrders.map((order, index) => (
                <TableRow key={index}>
                  <Text style={styles.tableCell}>{order.Order_No}</Text>
                  <Text style={styles.tableCell}>{order.reason_failure}</Text>
                </TableRow>
              ))}
            </View>
          </View>
        )}

        {active.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.subHeader}>Active Orders</Text>
            <View style={styles.table}>
              <TableRow>
                <Text style={styles.tableCellHeader}>Order ID</Text>
                <Text style={styles.tableCellHeader}>Receiver Name</Text>
                <Text style={styles.tableCellHeader}>Receiver Address</Text>
                <Text style={styles.tableCellHeader}>Amount</Text>
              </TableRow>
              {active.map((order, index) => (

                <TableRow key={index}>
                  <Text style={styles.tableCell}>{order.Order_No}</Text>
                  <Text style={styles.tableCell}>{order.Rec_Name}</Text>
                  <Text style={styles.tableCell}>{order.Rec_Add}</Text>
                  <Text style={styles.tableCell}>{order.amount}</Text>
                </TableRow>
              ))}
            </View>
          </View>
        )}

        {/* Footer Section */}
        <View style={styles.footer}>
          <Text>End of Report</Text>
          <Text>Generated at: {currentDate}</Text>
        </View>

        {/* Page Number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      </Page>
    </Document>
  );
};

export default PDFDocument;
